import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/app/auth/context/AuthContext';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import {
  IncidentService,
  type TriggeredIncidentListItem,
  type IncidentPaginationMeta,
} from '@/app/services/incident/incident.service';

const DEFAULT_PAGE = 1;

const emptyMeta: IncidentPaginationMeta = {
  total: 0,
  page: DEFAULT_PAGE,
  limit: 5,
  totalPages: 0,
};

export function useTriggeredIncidents() {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const debouncedSearchValue = useDebouncedValue(searchValue.trim());

  useEffect(() => {
    setPage(DEFAULT_PAGE);
  }, [debouncedSearchValue, sortOrder]);

  const query = useQuery({
    queryKey: [
      'incidents',
      'triggered',
      organizationId,
      page,
      debouncedSearchValue,
      sortOrder,
    ],
    queryFn: () =>
      IncidentService.listTriggered({
        page,
        q: debouncedSearchValue || undefined,
        sortOrder,
      }),
    enabled: !!organizationId,
  });

  const incidents = query.data?.data ?? [];
  const meta = query.data?.meta ?? emptyMeta;

  const handleSearchChange = (nextValue: string): void => {
    setSearchValue(nextValue);
  };

  const handlePageChange = (nextPage: number): void => {
    setPage(nextPage);
  };

  const toggleSortOrder = (): void => {
    setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'));
  };

  return {
    incidents,
    meta,
    error: (query.error ?? null) as Error | null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    searchValue,
    sortOrder,
    handleSearchChange,
    handlePageChange,
    toggleSortOrder,
    refetch: query.refetch,
  } satisfies {
    incidents: TriggeredIncidentListItem[];
    meta: IncidentPaginationMeta;
    error: Error | null;
    isFetching: boolean;
    isLoading: boolean;
    searchValue: string;
    sortOrder: 'asc' | 'desc';
    handleSearchChange: (nextValue: string) => void;
    handlePageChange: (nextPage: number) => void;
    toggleSortOrder: () => void;
    refetch: () => Promise<unknown>;
  };
}
